import { useState } from "react";
import { Check, Link2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { XLogo, WhatsAppLogo } from "./ShareBar";

const API = process.env.REACT_APP_BACKEND_URL;
/** Bot-friendly card link (server renders the live names wall as og:image); people are redirected to /founding. */
const SHARE_URL = `${API}/api/s/founding`;

const btn = "flex h-10 w-10 items-center justify-center rounded-full border border-border bg-bone text-ink transition-colors hover:border-ink hover:bg-sand";

/** "Share the wall" row for /founding — X, WhatsApp, native share, copy link. */
export const FoundingShareRow = ({ count }) => {
  const [copied, setCopied] = useState(false);
  const caption = `${count ? `${count} people` : "These people"} believed first. I'm on the Founding Donors wall at My Rich Aunt — a 501(c)(3) that awards direct grants to verified college students. Add your name:`;

  const open = (target) => window.open(target, "_blank", "noopener,noreferrer,width=640,height=680");
  const onX = () => open(`https://twitter.com/intent/tweet?${new URLSearchParams({ text: caption, url: SHARE_URL })}`);
  const onWhatsApp = () => open(`https://wa.me/?${new URLSearchParams({ text: `${caption} ${SHARE_URL}` })}`);
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const onNative = () => navigator.share({ title: "Founding Donors — My Rich Aunt", text: caption, url: SHARE_URL }).catch(() => {});
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${caption}\n\n${SHARE_URL}`);
      setCopied(true);
      toast.success("Link copied", { description: "Paste it in a text or post — it unfurls with the names wall." });
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Your browser blocked the copy — long-press the link to share it manually.");
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="founding-share-row">
      <span className="mr-1 text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">Share the wall</span>
      <button type="button" onClick={onX} aria-label="Share on X" title="Share on X" data-testid="founding-share-x" className={btn}><XLogo /></button>
      <button type="button" onClick={onWhatsApp} aria-label="Share on WhatsApp" title="Share on WhatsApp" data-testid="founding-share-whatsapp" className={btn}><WhatsAppLogo /></button>
      {canNativeShare && (
        <button type="button" onClick={onNative} aria-label="Share" title="Share" data-testid="founding-share-native" className={btn}>
          <Share2 className="h-4 w-4" strokeWidth={2} />
        </button>
      )}
      <span className="flex min-w-0 items-center gap-2 rounded-full border border-border bg-bone px-3 py-1.5 text-[13px]" title={SHARE_URL} data-testid="founding-share-link-preview">
        <Link2 className="h-3 w-3 shrink-0" strokeWidth={2} />
        <span className="truncate text-olive">myrichaunt.org/founding</span>
      </span>
      <button
        type="button"
        onClick={onCopy}
        data-testid="founding-share-copy"
        className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-[13px] font-semibold text-sand transition-colors hover:bg-forest"
      >
        {copied ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> : null}
        {copied ? "Copied" : "Copy link"}
      </button>
    </div>
  );
};
